import type {
  NotificationActionDescriptor,
  NotificationCategory,
  NotificationCopyDescriptor,
  NotificationIconDescriptor,
  NotificationItem,
  NotificationLinkDescriptor,
  NotificationViewItem,
  ReportPendingReviewItem,
} from "./types";

export interface NotificationResolverContext {
  isAdmin: boolean;
}

interface NotificationResolver<TItem extends NotificationItem = NotificationItem> {
  readonly types: ReadonlyArray<string>;
  getCategory(
    item: TItem,
    context: NotificationResolverContext,
  ): NotificationCategory;
  getTitle(item: TItem): NotificationCopyDescriptor;
  getMessage(item: TItem): NotificationCopyDescriptor;
  getIcon(item: TItem): NotificationIconDescriptor | null;
  getLink(item: TItem): NotificationLinkDescriptor | null;
  getActions(item: TItem): NotificationActionDescriptor[];
  shouldDisplay(item: TItem, context: NotificationResolverContext): boolean;
  getPriority(item: TItem, context: NotificationResolverContext): number;
}

const isNotificationCategory = (
  value: unknown,
): value is NotificationCategory => value === "action" || value === "info";

type NotificationParams = Record<string, unknown>;

const fallbackCopy = (
  item: NotificationItem,
  override?: NotificationCopyDescriptor,
): NotificationCopyDescriptor => {
  if (override) {
    return override;
  }

  const template = item.metadata?.template;
  if (template) {
    return {
      key: template,
      params: item.metadata?.params,
      fallback: item.message,
    };
  }

  return { fallback: item.message };
};

type CopyCase = {
  when: boolean;
  copy: NotificationCopyDescriptor;
};

class BaseNotificationResolver implements NotificationResolver {
  readonly types: ReadonlyArray<string> = ["*"];

  protected getParams(item: NotificationItem): NotificationParams {
    return item.metadata?.params ?? {};
  }

  protected getStringParam(
    item: NotificationItem,
    keys: readonly string[],
    options: { trim?: boolean } = { trim: true },
  ): string | undefined {
    const params = this.getParams(item);
    for (const key of keys) {
      const raw = params[key];
      if (typeof raw === "string") {
        const value = options.trim === false ? raw : raw.trim();
        if (value.length > 0) {
          return value;
        }
      }
    }

    return undefined;
  }

  protected extractDateRange(
    item: NotificationItem,
    overrides?: {
      startKeys?: readonly string[];
      endKeys?: readonly string[];
    },
  ) {
    const startKeys = overrides?.startKeys ?? ["startDate", "start"];
    const endKeys = overrides?.endKeys ?? ["endDate", "end"];

    return {
      start: this.getStringParam(item, startKeys),
      end: this.getStringParam(item, endKeys),
    };
  }

  protected resolveCopy(
    item: NotificationItem,
    cases: CopyCase[],
    fallback?: NotificationCopyDescriptor,
  ): NotificationCopyDescriptor {
    const match = cases.find((variant) => variant.when);
    if (match) {
      return match.copy;
    }

    return fallback ?? fallbackCopy(item);
  }

  getCategory(
    item: NotificationItem,
    context: NotificationResolverContext,
  ): NotificationCategory {
    void context;
    const candidate = item.metadata?.category;
    return isNotificationCategory(candidate) ? candidate : "info";
  }

  getTitle(item: NotificationItem): NotificationCopyDescriptor {
    void item;
    return {
      key: "notifications.system.title",
      fallback: "System notification",
    };
  }

  getMessage(item: NotificationItem): NotificationCopyDescriptor {
    return fallbackCopy(item);
  }

  getIcon(item: NotificationItem): NotificationIconDescriptor | null {
    void item;
    return null;
  }

  getLink(item: NotificationItem): NotificationLinkDescriptor | null {
    void item;
    return null;
  }

  getActions(item: NotificationItem): NotificationActionDescriptor[] {
    void item;
    return [];
  }

  shouldDisplay(
    item: NotificationItem,
    context: NotificationResolverContext,
  ): boolean {
    void item;
    void context;
    return true;
  }

  getPriority(
    item: NotificationItem,
    context: NotificationResolverContext,
  ): number {
    return this.getCategory(item, context) === "action"
      ? context.isAdmin
        ? 2
        : 1
      : 0;
  }
}

class ReportPendingReviewResolver
  extends BaseNotificationResolver
  implements NotificationResolver<ReportPendingReviewItem>
{
  override readonly types = ["report.pending_review"] as const;

  override getCategory(): NotificationCategory {
    return "action";
  }

  override getTitle(): NotificationCopyDescriptor {
    return {
      key: "notifications.report.pendingReview.title",
      fallback: "Report validation",
    };
  }

  override getMessage(
    item: ReportPendingReviewItem,
  ): NotificationCopyDescriptor {
    const ownerName =
      this.getStringParam(item, ["ownerName", "reportOwner"]) ??
      item.requester?.name?.trim();
    const period = this.getStringParam(item, ["period", "range"]);
    const reportName = this.getStringParam(item, ["reportName"]);

    if (!ownerName && !period && !reportName) {
      return fallbackCopy(item);
    }

    const cases: CopyCase[] = [];

    if (ownerName && reportName && period) {
      cases.push({
        when: true,
        copy: {
          key: "notifications.report.pendingReview.detailFull",
          params: { reportName, ownerName, period },
          fallback: `Report ${reportName} by ${ownerName} for period ${period} is awaiting validation.`,
        },
      });
    }

    if (ownerName && period) {
      cases.push({
        when: true,
        copy: {
          key: "notifications.report.pendingReview.detailOwnerPeriod",
          params: { ownerName, period },
          fallback: `Report by ${ownerName} for period ${period} is awaiting validation.`,
        },
      });
    }

    if (ownerName && reportName) {
      cases.push({
        when: true,
        copy: {
          key: "notifications.report.pendingReview.detailOwnerReport",
          params: { reportName, ownerName },
          fallback: `Report ${reportName} by ${ownerName} is awaiting validation.`,
        },
      });
    }

    if (reportName && period) {
      cases.push({
        when: true,
        copy: {
          key: "notifications.report.pendingReview.detailReportPeriod",
          params: { reportName, period },
          fallback: `Report ${reportName} for period ${period} is awaiting validation.`,
        },
      });
    }

    if (ownerName) {
      cases.push({
        when: true,
        copy: {
          key: "notifications.report.pendingReview.detailOwner",
          params: { ownerName },
          fallback: `Report by ${ownerName} is awaiting validation.`,
        },
      });
    }

    if (reportName) {
      cases.push({
        when: true,
        copy: {
          key: "notifications.report.pendingReview.detailReport",
          params: { reportName },
          fallback: `Report ${reportName} is awaiting validation.`,
        },
      });
    }

    if (period) {
      cases.push({
        when: true,
        copy: {
          key: "notifications.report.pendingReview.detailPeriod",
          params: { period },
          fallback: `Report for period ${period} is awaiting validation.`,
        },
      });
    }

    return this.resolveCopy(item, cases, {
      key: "notifications.report.pendingReview.detailFallback",
      fallback: item.message,
    });
  }

  override getIcon(): NotificationIconDescriptor | null {
    return { name: "file-text", accent: "default" };
  }

  override getPriority(
    item: ReportPendingReviewItem,
    context: NotificationResolverContext,
  ): number {
    void item;
    return context.isAdmin ? 15 : 5;
  }
}

class ReportValidatedResolver
  extends BaseNotificationResolver
  implements NotificationResolver
{
  override readonly types = ["reports.validated", "report.validated"] as const;

  override getCategory(): NotificationCategory {
    return "info";
  }

  override getTitle(): NotificationCopyDescriptor {
    return {
      key: "notifications.reports.validated.title",
      fallback: "Report validated",
    };
  }

  override getMessage(item: NotificationItem): NotificationCopyDescriptor {
    const { start: startDate, end: endDate } = this.extractDateRange(item);
    const period = this.getStringParam(item, ["period", "range"]);

    if (startDate && endDate) {
      return {
        key: "notifications.reports.validated.message",
        params: { startDate, endDate },
        fallback: `Your report from ${startDate} to ${endDate} has been validated.`,
      };
    }

    if (period) {
      return {
        fallback: `Your report for ${period} has been validated.`,
      };
    }

    return fallbackCopy(item, {
      key: "notifications.reports.validated.message",
      fallback: "Your report has been validated.",
    });
  }

  override getIcon(): NotificationIconDescriptor | null {
    return { name: "circle-check", accent: "success" };
  }
}

class ReportRejectedResolver
  extends BaseNotificationResolver
  implements NotificationResolver
{
  override readonly types = ["reports.rejected", "report.rejected"] as const;

  override getCategory(): NotificationCategory {
    return "info";
  }

  override getTitle(): NotificationCopyDescriptor {
    return {
      key: "notifications.reports.rejected.title",
      fallback: "Report rejected",
    };
  }

  override getMessage(item: NotificationItem): NotificationCopyDescriptor {
    return fallbackCopy(item, {
      key: "notifications.reports.rejected.message",
      fallback: "Your activity report has been rejected or deleted.",
    });
  }

  override getIcon(): NotificationIconDescriptor | null {
    return { name: "circle-alert", accent: "danger" };
  }
}

class AbsenceNotificationResolver
  extends BaseNotificationResolver
  implements NotificationResolver
{
  override readonly types = [
    "absence.pending_approval",
    "absence.approved",
    "absence.rejected",
    "absence.updated",
    "absence.cancelled",
    "vacation.pending_approval",
    "vacation.approved",
    "vacation.cancelled",
  ] as const;

  override getCategory(
    item: NotificationItem,
    context: NotificationResolverContext,
  ): NotificationCategory {
    void context;
    return item.type.endsWith("pending_approval") ? "action" : "info";
  }

  override getTitle(item: NotificationItem): NotificationCopyDescriptor {
    switch (item.type) {
      case "vacation.pending_approval":
        return {
          key: "notifications.vacation.request.title",
          fallback: "Vacation request",
        };
      case "vacation.approved":
        return {
          key: "notifications.vacation.approved.title",
          fallback: "Vacation approved",
        };
      case "vacation.cancelled":
        return {
          key: "notifications.vacation.cancelled.title",
          fallback: "Vacation cancelled",
        };
      case "absence.pending_approval":
        return {
          key: "notifications.absences.pending_approval.title",
          fallback: "Absence request",
        };
      case "absence.approved":
        return {
          key: "notifications.absences.approved.title",
          fallback: "Request approved",
        };
      case "absence.rejected":
        return {
          key: "notifications.absences.rejected.title",
          fallback: "Request rejected",
        };
      case "absence.cancelled":
        return {
          key: "notifications.absences.cancelled.title",
          fallback: "Request cancelled",
        };
      default:
        return {
          key: "notifications.absences.title",
          fallback: "Absence update",
        };
    }
  }

  override getMessage(item: NotificationItem): NotificationCopyDescriptor {
    const requesterName =
      this.getStringParam(item, ["requesterName"]) ??
      item.requester?.name?.trim();
    const period = this.getStringParam(item, ["period", "range"]);
    const type = this.getStringParam(item, ["type", "absenceType"]);
    const { start: startDate, end: endDate } = this.extractDateRange(item);

    if (item.type === "vacation.pending_approval") {
      if (requesterName && startDate && endDate) {
        return {
          key: "notifications.vacation.request.detailRange",
          params: { requesterName, startDate, endDate },
          fallback: `${requesterName} requested vacation from ${startDate} to ${endDate}.`,
        };
      }

      if (startDate && endDate) {
        return {
          key: "notifications.vacation.request.detailRangeNoName",
          params: { startDate, endDate },
          fallback: `Vacation requested from ${startDate} to ${endDate}.`,
        };
      }

      if (requesterName && period) {
        return {
          key: "notifications.vacation.request.detailPeriod",
          params: { requesterName, period },
          fallback: `${requesterName} requested vacation for ${period}.`,
        };
      }

      if (period) {
        return {
          key: "notifications.vacation.request.detailPeriodNoName",
          params: { period },
          fallback: `Vacation requested for ${period}.`,
        };
      }

      if (requesterName) {
        return {
          key: "notifications.vacation.request.detailRequester",
          params: { requesterName },
          fallback: `${requesterName} submitted a vacation request.`,
        };
      }

      return fallbackCopy(item, {
        key: "notifications.vacation.request.detailFallback",
        fallback: "A new vacation request is waiting for review.",
      });
    }

    if (item.type === "vacation.approved" || item.type === "vacation.cancelled") {
      const messageKey =
        item.type === "vacation.approved"
          ? "notifications.vacation.approved.message"
          : "notifications.vacation.cancelled.message";

      if (startDate && endDate) {
        return {
          key: messageKey,
          params: { startDate, endDate },
          fallback:
            item.type === "vacation.approved"
              ? `Your vacation from ${startDate} to ${endDate} has been approved.`
              : `Your vacation from ${startDate} to ${endDate} has been canceled.`,
        };
      }
    }

    if (item.type === "absence.pending_approval") {
      if (requesterName && type && startDate && endDate) {
        return {
          key: "notifications.absences.pending_approval.range",
          params: { requesterName, type, startDate, endDate },
          fallback: `${requesterName} has requested ${type} from ${startDate} to ${endDate}.`,
        };
      }

      if (requesterName && type && startDate) {
        return {
          key: "notifications.absences.pending_approval.single",
          params: { requesterName, type, startDate },
          fallback: `${requesterName} has requested ${type} for ${startDate}.`,
        };
      }

      if (requesterName && period) {
        return {
          key: "notifications.absences.pending_approval.detailPeriod",
          params: { requesterName, period },
          fallback: `${requesterName} requested an absence for ${period}.`,
        };
      }

      if (requesterName) {
        return {
          key: "notifications.absences.pending_approval.detailRequester",
          params: { requesterName },
          fallback: `${requesterName} submitted an absence request.`,
        };
      }

      return fallbackCopy(item, {
        key: "notifications.absences.pending_approval.detailFallback",
        fallback: "A new absence request is waiting for review.",
      });
    }

    if (
      item.type === "absence.approved" ||
      item.type === "absence.rejected" ||
      item.type === "absence.cancelled"
    ) {
      const baseKey =
        item.type === "absence.approved"
          ? "notifications.absences.approved"
          : item.type === "absence.rejected"
            ? "notifications.absences.rejected"
            : "notifications.absences.cancelled";

      if (type && startDate && endDate) {
        return {
          key: `${baseKey}.range`,
          params: { type, startDate, endDate },
          fallback:
            item.type === "absence.approved"
              ? `Your ${type} request from ${startDate} to ${endDate} has been approved.`
              : item.type === "absence.rejected"
                ? `Your ${type} request from ${startDate} to ${endDate} has been rejected.`
                : `Your ${type} request from ${startDate} to ${endDate} has been canceled.`,
        };
      }

      if (type && startDate) {
        return {
          key: `${baseKey}.single`,
          params: { type, startDate },
          fallback:
            item.type === "absence.approved"
              ? `Your ${type} request for ${startDate} has been approved.`
              : item.type === "absence.rejected"
                ? `Your ${type} request for ${startDate} has been rejected.`
                : `Your ${type} request for ${startDate} has been canceled.`,
        };
      }
    }

    return fallbackCopy(item, {
      fallback: item.message || "An absence notification was received.",
    });
  }

  override getIcon(item: NotificationItem): NotificationIconDescriptor | null {
    if (item.type.endsWith("approved")) {
      return { name: "calendar-check-2", accent: "success" };
    }
    if (item.type.endsWith("rejected") || item.type.endsWith("cancelled")) {
      return { name: "calendar-x-2", accent: "danger" };
    }
    return { name: "calendar-clock", accent: "warning" };
  }

  override getPriority(
    item: NotificationItem,
    context: NotificationResolverContext,
  ): number {
    return this.getCategory(item, context) === "action" ? 14 : 6;
  }
}

class NotificationFactory {
  private static readonly defaultResolver = new BaseNotificationResolver();
  private static readonly registry = new Map<string, NotificationResolver>();

  static register(...resolvers: NotificationResolver[]) {
    resolvers.forEach((resolver) => {
      resolver.types.forEach((type) => {
        this.registry.set(type, resolver);
      });
    });
  }

  static getResolver(item: NotificationItem): NotificationResolver {
    return this.registry.get(item.type) ?? this.defaultResolver;
  }
}

export const resolveNotificationItem = (
  item: NotificationItem,
  context: NotificationResolverContext,
): NotificationViewItem | null => {
  const resolver = NotificationFactory.getResolver(item);
  if (!resolver.shouldDisplay(item, context)) {
    return null;
  }

  const category = resolver.getCategory(item, context);
  const view = {
    title: resolver.getTitle(item),
    message: resolver.getMessage(item),
    icon: resolver.getIcon(item),
    link: resolver.getLink(item),
    actions: resolver.getActions(item),
    priority: resolver.getPriority(item, context),
  };

  return {
    ...item,
    category,
    view,
  };
};

NotificationFactory.register(
  new ReportPendingReviewResolver(),
  new ReportValidatedResolver(),
  new ReportRejectedResolver(),
  new AbsenceNotificationResolver(),
);
